import React from 'react';
import { Translation } from 'react-i18next';
import { notify } from '../components/toast';
import CaredoveIntegrationService from '../services/caredove-integration-service';
import authService from '../services/auth-service';
import { fetchAllResourcesExclParams } from '../actions/resources';
import type { AuthState } from '../store/reducers/auth';
import type { Action } from '../types/actions';
import { reLoadAuth, authenticate, setTfaChallenge, setFormErrors } from './auth';
import history from '../history';
import userActions from '../actions/user';
import { getRouteTFunction } from '../i18n';

type Dispatch = (action: Action | ThunkAction | PromiseAction) => any;
type PromiseAction = Promise<Action>;
type GetState = () => {
  auth: AuthState,
  user: any,
};
type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;

const toastMessage = (key: String, options: Object) => (
  <Translation ns='caredoveActions'>{(t) => t(key, options)}</Translation>
);

function sendToast(message, type, containerIds): Action {
  notify(message, type, containerIds);
  return {
    type: 'NOTIFY'
  };
}

function connectFailure(error, code): Action {
  const customDisplayCodes = [ // these messages can be displayed in the notification
    'referral_already_imported'
  ];
  const errMsg = code && customDisplayCodes.includes(code) ?
    toastMessage('connect_failure', { msg: error }) :
    toastMessage('connect_failure_default');
  sendToast(errMsg, 'error');
  return {
    type: 'CAREDOVE_CONNECT_ERROR',
    error
  };
}

function connect(msg): Action {
  if (!msg) {
    sendToast(toastMessage('connect_default'), 'info');
  } else {
    sendToast(msg, 'info');
  }
  return {
    type: 'CAREDOVE_CONNECT'
  };
}

function connectSuccess(msg): Action {
  if (!msg) {
    sendToast(toastMessage('connect_success_default'), 'success');
  } else {
    sendToast(msg, 'info');
  }
  return {
    type: 'CAREDOVE_CONNECT_SUCCESS'
  };
}

export function login(email: string, password: string, tfaToken: ?string, remember: boolean): ThunkAction {
  return ((dispatch) => {
    dispatch(reLoadAuth());
    authService.login(email, password, tfaToken, remember)
      .then((data) => {
        if (data.access_token) {
          dispatch(authenticate());
        } else if (data.tfa_enabled) {
          dispatch(setTfaChallenge());
        }
      })
      .catch((error) => {
        if (error && error.length > 0 && (error[0].code === '2fa_required')) {
          dispatch(setTfaChallenge());
        } else {
          dispatch(setFormErrors(error));
        }
      });
  });
}

export function signup(email: string, password: string): ThunkAction {
  return ((dispatch) => {
    dispatch(reLoadAuth());
    return authService.signup(email, password)
      .then(() => {
        dispatch(authenticate());
        dispatch(userActions.createUser(email, false));
      })
      .catch((error) => dispatch(setFormErrors(error)));
  });
}

export function connectCaredove(token: string, socket: any): ThunkAction {
  return (async (dispatch) => {
    const t = await getRouteTFunction();
    history.push(t('redirectPaths./dashboard'));
    // Send the socket id of this session to the server so it knows which socket to send messages to
    CaredoveIntegrationService.connectCaredove(token, socket.id)
      .then(() => {
        dispatch(connect());
        socket.on('caredove_import', async (data) => {
          const importStatus = JSON.parse(data);
          // This code is not working all the time, even though backend is sending 'importing' message,
          // so dispatch connect() immediately in above then() instead
          /*
            if (importStatus.status === 'importing') {
              // The import job was initiated
              dispatch(connect());
            }
          */
          if (importStatus.status === 'success') {
            // A new resource was successfully imported, so try to refetch all of the user's resources
            try {
              await dispatch(fetchAllResourcesExclParams('nested'));
              dispatch(connectSuccess());
              socket.disconnect();
            } catch (error) {
              console.log(error);
              dispatch(connectSuccess(toastMessage('import_success')));
              socket.disconnect();
            }
          }
          if (importStatus.status === 'error') {
            console.log(importStatus.error);
            dispatch(connectFailure(importStatus.error));
            socket.disconnect();
          }
        });
      })
      .catch((error) => {
        // There was an issue using the Caredove integration service
        if (typeof (error) === 'string') {
          dispatch(connectFailure(error));
        } else {
          dispatch(connectFailure(error.message, error.code));
        }
        socket.disconnect();
      });
  });
}

export default connectCaredove;



// WEBPACK FOOTER //
// ./src/actions/caredove-login.js